import { api } from "./client";

/**
 * Metadata projection of a precursor document, as returned by the list
 * endpoints. `content` is deliberately absent — list responses must never
 * carry the document bodies.
 *
 * Mirrors `DependencyDocumentMeta` in
 * `packages/api/src/schemas/dependencyDocument.ts`.
 */
export interface DependencyDocumentMeta {
  id: string;
  fileName: string;
  originalPath: string | null;
  contentType: string;
  sizeBytes: number;
  sha256: string;
  label: string | null;
  epicRequestId: string | null;
  epicId: string | null;
  /** Joined for display, e.g. "ER-12". */
  epicRequestIdentifier?: string | null;
  /** Joined for display, e.g. "PI-SPEC-SW-E193". */
  epicIdentifier?: string | null;
  uploadedById: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * A precursor document including its text body. Only returned by the
 * single-document endpoint.
 */
export interface DependencyDocumentWithContent extends DependencyDocumentMeta {
  content: string;
}

/**
 * Read-only client for precursor ("dependency") documents.
 *
 * Upload and delete are intentionally absent: the agent flow is the only
 * intended producer, so the browser surface is read-only.
 */
export const dependencyDocumentsApi = {
  /** List the documents attached to an Epic Request. Metadata only. */
  listForEpicRequest: (id: string) =>
    api.get<{ data: DependencyDocumentMeta[] }>(
      `/epic-requests/${id}/dependencies`
    ),

  /** List the documents attached to an Epic. Metadata only. */
  listForEpic: (id: string) =>
    api.get<{ data: DependencyDocumentMeta[] }>(`/epics/${id}/dependencies`),

  /** Fetch one document including its text content. */
  get: (depId: string) =>
    api.get<{ data: DependencyDocumentWithContent }>(`/dependencies/${depId}`),

  /**
   * Fetch the raw document body as a Blob.
   *
   * Goes through the authenticated client rather than a bare anchor href
   * because the endpoint requires an `Authorization: Bearer` header.
   */
  downloadBlob: (depId: string) =>
    api.getBlob(`/dependencies/${depId}/content`),
};

/**
 * Format a byte count for display, e.g. `1.4 KB`, `2.1 MB`.
 *
 * Exported alongside the client because both the panel and its tests need the
 * exact same formatting rule.
 */
export function formatDocumentSize(sizeBytes: number): string {
  if (!Number.isFinite(sizeBytes) || sizeBytes < 0) return "—";
  if (sizeBytes < 1024) return `${String(sizeBytes)} B`;
  const kb = sizeBytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

/**
 * Fetch a document's raw body and save it to disk under exactly `fileName`.
 *
 * Mirrors the blob-download approach already used by
 * `useAuthenticatedDownload` in `src/hooks/use-authenticated-image.ts`, but
 * routes the fetch through the `api` client so token refresh and the 401 retry
 * are handled in one place.
 */
export async function saveDependencyDocument(
  depId: string,
  fileName: string
): Promise<void> {
  const blob = await dependencyDocumentsApi.downloadBlob(depId);
  const blobUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = blobUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}
